package web.fstech.onthecoffee.resources

import web.fstech.onthecoffee.privacy.AppLanguage

data class Strings(
    // App & Branding
    val appName: String,
    val appTitle: String,
    val packageName: String,
    val supportEmail: String,
    val copyright: String,
    val platformSupport: String,

    // Navigation Buttons
    val navHome: String,
    val navSignIn: String,
    val navSignUp: String,
    val navForgotPassword: String,
    val navDeleteAccount: String,
    val navPrivacyPolicy: String,
    val navLangEng: String,
    val navLangAr: String,

    // Landing Page
    val heroTitle: String,
    val heroSubtitle: String,
    val heroCtaGetStarted: String,
    val heroCtaPrivacy: String,

    val featuresSectionTitle: String,
    val feature1Title: String,
    val feature1Desc: String,
    val feature2Title: String,
    val feature2Desc: String,
    val feature3Title: String,
    val feature3Desc: String,

    val quickLinksTitle: String,
    val quickLinkHome: String,
    val quickLinkPrivacy: String,
    val quickLinkSignIn: String,
    val quickLinkSignUp: String,
    val quickLinkForgot: String,
    val quickLinkDelete: String,

    // Auth Pages
    val authPortalTitle: String,
    val signInTitle: String,
    val signUpTitle: String,
    val forgotTitle: String,
    val deleteTitle: String,
    val deleteWarning: String,
    val deleteConfirmCheck: String,

    val labelEmail: String,
    val labelPassword: String,
    val btnSignIn: String,
    val btnSignUp: String,
    val btnSendResetLink: String,
    val btnDeleteMyAccount: String,

    // Google Play Store
    val btnGooglePlay: String,
    val googlePlaySubtitle: String
)

object AppStrings {
    val EN = Strings(
        appName = "OnTheCoffee (على القهوة)",
        appTitle = "OnTheCoffee (على القهوة) - Official Website",
        packageName = "client.digital.onthecoffee",
        supportEmail = "support@onthecoffee.digital",
        copyright = "© 2025 OnTheCoffee (على القهوة). All rights reserved.",
        platformSupport = "Package Name: client.digital.onthecoffee | Supported on Android, Web & iOS",

        navHome = "Home",
        navSignIn = "Sign In",
        navSignUp = "Sign Up",
        navForgotPassword = "Forgot Password",
        navDeleteAccount = "Delete Account",
        navPrivacyPolicy = "Privacy Policy",
        navLangEng = "English",
        navLangAr = "العربية",

        heroTitle = "Welcome to OnTheCoffee (على القهوة)",
        heroSubtitle = "Brewing Ideas & Competing in the Ultimate Business & Strategy Game!",
        heroCtaGetStarted = "Get Started / Sign Up",
        heroCtaPrivacy = "Read Privacy Policy",

        featuresSectionTitle = "Game Features & Business Arena",
        feature1Title = "☕ Café Simulator",
        feature1Desc = "Build and manage your dream coffee shop in an interactive simulation.",
        feature2Title = "🤖 AI Business Canvas",
        feature2Desc = "Powered by Google Gemini AI on Supabase Edge Functions to evaluate business ideas.",
        feature3Title = "🏆 Global Leaderboards",
        feature3Desc = "Compete with players worldwide and showcase your business strategy skills.",

        quickLinksTitle = "Site Map & Quick Links",
        quickLinkHome = "Home Page",
        quickLinkPrivacy = "Privacy Policy & Data Safety",
        quickLinkSignIn = "Sign In to Account",
        quickLinkSignUp = "Create New Account",
        quickLinkForgot = "Forgot Password / Recovery",
        quickLinkDelete = "Delete Account & Data Request",

        authPortalTitle = "OnTheCoffee Account Management",
        signInTitle = "Sign In to Your Account",
        signUpTitle = "Create New Account",
        forgotTitle = "Reset Password",
        deleteTitle = "Delete Account & Data",
        deleteWarning = "Warning: This action is permanent and will remove all your data associated with OnTheCoffee.",
        deleteConfirmCheck = "I confirm that I want to permanently delete my account.",

        labelEmail = "Email",
        labelPassword = "Password",
        btnSignIn = "Sign In",
        btnSignUp = "Sign Up",
        btnSendResetLink = "Send Reset Link",
        btnDeleteMyAccount = "Delete My Account",
        btnGooglePlay = "Get it on Google Play",
        googlePlaySubtitle = "Download OnTheCoffee Game on Android"
    )

    val AR = Strings(
        appName = "على القهوة (OnTheCoffee)",
        appTitle = "على القهوة (OnTheCoffee) - الموقع الرسمي",
        packageName = "client.digital.onthecoffee",
        supportEmail = "support@onthecoffee.digital",
        copyright = "© 2025 على القهوة (OnTheCoffee). جميع الحقوق محفوظة.",
        platformSupport = "معرف التطبيق: client.digital.onthecoffee | مدعوم على أندرويد، الويب و iOS",

        navHome = "الرئيسية",
        navSignIn = "تسجيل الدخول",
        navSignUp = "إنشاء حساب",
        navForgotPassword = "استعادة كلمة المرور",
        navDeleteAccount = "حذف الحساب",
        navPrivacyPolicy = "سياسة الخصوصية",
        navLangEng = "English",
        navLangAr = "العربية",

        heroTitle = "مرحباً بكم في لعبة «على القهوة» (OnTheCoffee)",
        heroSubtitle = "صناعة الأفكار والتنافس في لعبة إدارة الأعمال والتخطيط الاستراتيجي الأكبر!",
        heroCtaGetStarted = "ابدأ الآن / سجل حسابك",
        heroCtaPrivacy = "قراءة سياسة الخصوصية",

        featuresSectionTitle = "مميزات اللعبة وساحة الأعمال",
        feature1Title = "☕ محاكاة مقهى الأحلام",
        feature1Desc = "ابنِ وادِر مقهاك الخاص في محاكاة تفاعلية مشوقة.",
        feature2Title = "🤖 نموذج العمل التجاري بالذكاء الاصطناعي",
        feature2Desc = "مدعوم بنموذج Google Gemini AI لتقييم وتطوير أفكار المشاريع الريادية.",
        feature3Title = "🏆 لوحة الشرف العالمية",
        feature3Desc = "تنافس مع لاعبين من مختلف أنحاء العالم واعرض مهاراتك القيادية.",

        quickLinksTitle = "خريطة الموقع والروابط السريعة",
        quickLinkHome = "الصفحة الرئيسية",
        quickLinkPrivacy = "سياسة الخصوصية وأمان البيانات",
        quickLinkSignIn = "تسجيل الدخول للحساب",
        quickLinkSignUp = "إنشاء حساب جديد",
        quickLinkForgot = "استعادة كلمة المرور",
        quickLinkDelete = "طلب حذف الحساب والبيانات",

        authPortalTitle = "إدارة حساب «على القهوة»",
        signInTitle = "تسجيل الدخول إلى حسابك",
        signUpTitle = "إنشاء حساب جديد",
        forgotTitle = "إعادة ضبط كلمة المرور",
        deleteTitle = "حذف الحساب والبيانات",
        deleteWarning = "تحذير: هذا الإجراء نهائي وسيتم حذف جميع البيانات المرتبطة بحسابك في OnTheCoffee.",
        deleteConfirmCheck = "أؤكد رغبتي في حذف حسابي وجميع بياناتي نهائياً.",

        labelEmail = "البريد الإلكتروني",
        labelPassword = "كلمة المرور",
        btnSignIn = "تسجيل الدخول",
        btnSignUp = "إنشاء الحساب",
        btnSendResetLink = "إرسال رابط الضبط",
        btnDeleteMyAccount = "حذف حسابي نهائياً",
        btnGooglePlay = "حملها من متجر جوجل بلاي",
        googlePlaySubtitle = "حمل لعبة «على القهوة» لنظام أندرويد"
    )

    fun get(language: AppLanguage): Strings = when (language) {
        AppLanguage.ENGLISH -> EN
        AppLanguage.ARABIC -> AR
    }
}
